import type { FC } from 'react'
import { useEffect, useState } from 'react'
import Carousel from '@acctglobal/carousel-universal'
import { useMediaQuery } from 'react-responsive'
import useWindowDimensions from 'src/components/hooks/useWindowDimensions'

import DynamicImages from '../CarouselBanner/DynamicImage'

type Props = {
  hasSpacingOnBottom?: boolean
  hasSpacingOnTop?: boolean
  contentLayout?: string
  allItems?: TValues[]
  useBullet?: boolean
  useBulletMobile?: boolean
  useArrows?: boolean
  valueLoading?: 'eager' | 'lazy'
  gapValue?: number
  titleDesktop?: string
  titleMobile?: string
  locations?: any
}

const IMAGE_DEFAULT =
  'https://tatauyqa.vtexassets.com/arquivos/teste-banner.png'

const PromotionalBanner: FC<Props> = ({
  allItems,
  contentLayout = 'Mobile 1 / Desktop 1',
  hasSpacingOnBottom,
  hasSpacingOnTop,
  useBullet,
  useBulletMobile,
  useArrows,
  titleDesktop,
  titleMobile,
  valueLoading,
  gapValue,
  locations = [],
}) => {
  const [items, setItems] = useState<number[]>([])
  const [imageWidth, setImageWidth] = useState<any>()
  const [imageHeight, setImageHeight] = useState<any>()
  const [currentCity, setCurrentCity] = useState<string | null>(null)

  const className = `${hasSpacingOnTop ? 'paddingTop' : ''} ${
    hasSpacingOnBottom ? 'paddingBottom' : ''
  }`

  useEffect((): void => {
    switch (contentLayout) {
      case 'Mobile 1 / Desktop 1': {
        setItems([1, 1])
        setImageWidth({
          desktop: 1280,
          mobile: 528,
        })
        setImageHeight({
          desktop: 212,
          mobile: 180,
        })

        break
      }

      case 'Mobile 1 / Desktop 2': {
        setItems([1, 2])
        setImageWidth({
          desktop: 600,
          mobile: 528,
        })

        setImageHeight({
          desktop: 212,
          mobile: 180,
        })

        break
      }

      case 'Mobile 1 / Desktop 3': {
        setItems([1, 3])
        setImageWidth({
          desktop: 420,
          mobile: 528,
        })

        setImageHeight({
          desktop: 283,
          mobile: 180,
        })

        break
      }

      case 'Mobile 1 / Desktop 4': {
        setItems([1, 4])
        setImageWidth({
          desktop: 420,
          mobile: 528,
        })

        setImageHeight({
          desktop: 283,
          mobile: 180,
        })

        break
      }

      case 'Mobile 2 / Desktop 1': {
        setItems([2, 1])
        setImageWidth({
          desktop: 1280,
          mobile: 300,
        })

        setImageHeight({
          desktop: 212,
          mobile: 255,
        })

        break
      }

      case 'Mobile 2 / Desktop 2': {
        setItems([2, 2])
        setImageWidth({
          desktop: 600,
          mobile: 300,
        })

        setImageHeight({
          desktop: 212,
          mobile: 255,
        })

        break
      }

      case 'Mobile 2 / Desktop 3': {
        setItems([2, 3])
        setImageWidth({
          desktop: 420,
          mobile: 300,
        })

        setImageHeight({
          desktop: 283,
          mobile: 255,
        })

        break
      }

      case 'Mobile 2 / Desktop 4': {
        setItems([2, 4])
        setImageWidth({
          desktop: 420,
          mobile: 300,
        })

        setImageHeight({
          desktop: 283,
          mobile: 255,
        })

        break
      }

      default:
        throw new Error(`Action not implemented`)
    }
  }, [contentLayout])

  const isDesktop = useMediaQuery({
    query: '(min-width: 768px)',
  })

  const { isMobile } = useWindowDimensions()

  const itemsQuantity = !isDesktop ? items[0] : items[1]

  useEffect(() => {
    const storedCity = JSON.parse(localStorage.getItem('Location') || '{}')
    setCurrentCity(storedCity?.description || null)
  }, [])

  if (!allItems) {
    return null
  }

  const title = !isDesktop ? titleMobile : titleDesktop

  const filteredBanners = allItems?.map((item) => {
    const desktopSource = item?.sources?.filter(
      ({ media }) => media === '(min-width: 40em)'
    )

    const mobileSource = item?.sources?.filter(
      ({ media }) => media === '(max-width: 40em)'
    )

    const banners = isDesktop ? desktopSource : mobileSource

    return banners?.filter(() => {
      if (!locations || locations.every((loc: any) => loc === null)) {
        return true
      }

      if (locations.includes('Seleccionar todos')) {
        return true
      }

      return locations.includes(currentCity)
    })
  })

  const shouldShowBullets =
    useBullet === true && filteredBanners?.flat().length > 1

  const shouldShowBulletsMobile =
    useBulletMobile === true && filteredBanners?.flat().length > 1

  return (
    <div className={`promotion__container ${className}`}>
      {title && (
        <div className="promotion__wrapper">
          <div className="promotion__link">
            <h2 className="promotion__title">{title}</h2>
          </div>
        </div>
      )}
      {itemsQuantity !== undefined && (
        <Carousel
          qtyItems={itemsQuantity}
          arrow={
            isMobile
              ? { isVisible: false }
              : {
                  isVisible: useArrows === true,
                  iconColor: 'black',
                  style: {
                    margin: 0,
                    paddingInline: 12,
                    width: 24,
                    height: 24,
                  },
                }
          }
          gapItems={gapValue ?? 8}
          bullet={{
            isVisible: isDesktop ? shouldShowBullets : shouldShowBulletsMobile,
            bulletDisableColor: '#FFF',
            bulletEnableColor: '#E5002B',
            style: {
              marginRight: '7px',
              border: '1px solid #E5002B',
              borderRadius: '30px',
              width: isMobile ? 6 : 12,
              height: isMobile ? 6 : 12,
            },
          }}
        >
          {filteredBanners?.flat()?.map(({ href, alt, srcSet }, idx) => (
            <div key={idx}>
              {imageWidth && imageHeight && (
                <DynamicImages
                  valueLoading={valueLoading}
                  imageValues={{
                    href: srcSet ?? IMAGE_DEFAULT,
                    title: alt,
                    id: `${className} banner home ${idx}`,
                    link: href,
                  }}
                  imageWidth={imageWidth}
                  imageHeight={imageHeight}
                />
              )}
            </div>
          ))}
        </Carousel>
      )}
    </div>
  )
}

export default PromotionalBanner
